import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import { Card, Col, Row, Button, Nav, Badge, Spinner } from "react-bootstrap";
import { Link, useParams } from "react-router-dom";
import { getToken } from '../services/authorize';
import axiosInstance from "../services/axiosInterceptor";
import Swal from "sweetalert2";
import { useLoading } from '../contexts/LoadingContext';
import jsPDF from "jspdf";

const API = process.env.REACT_APP_API;

const GRADE_THRESHOLDS = [
  { min: 80, grade: 4 }, { min: 75, grade: 3.5 }, { min: 70, grade: 3 },
  { min: 65, grade: 2.5 }, { min: 60, grade: 2 }, { min: 55, grade: 1.5 },
  { min: 50, grade: 1 },  { min: 0, grade: 0 },
];

const calcGrade = (score) => {
  if (score === null || score === undefined || score === '') return null;
  const s = parseFloat(score);
  if (isNaN(s)) return null;
  for (const t of GRADE_THRESHOLDS) { if (s >= t.min) return t.grade; }
  return 0;
};

const calcGPA = (subjectGrades, subjects) => {
  let pts = 0, cred = 0;
  subjects.forEach(sub => {
    const entry = subjectGrades[sub.sub_id];
    const g = entry ? calcGrade(entry.g_grade) : null;
    if (g !== null && sub.sub_credit) { pts += g * Number(sub.sub_credit); cred += Number(sub.sub_credit); }
  });
  return cred > 0 ? (pts / cred).toFixed(2) : '-';
};

export default function ViewClassGrade() {
  const { room_id, year_id } = useParams();
  const { setLoading } = useLoading();
  const currentSkin = localStorage.getItem('skin-mode') ? 'dark' : '';
  const [skin, setSkin] = useState(currentSkin);
  const printRef = useRef(null);

  const switchSkin = (skin) => {
    const tables = document.getElementsByTagName('table');
    for (const table of tables) {
      const theads = table.querySelectorAll('thead');
      if (skin === 'dark') {
        for (const thead of theads) { thead.classList.remove('table-secondary'); thead.classList.add('table-primary'); }
        table.classList.add('table-dark');
      } else {
        table.classList.remove('table-dark');
        for (const thead of theads) { thead.classList.remove('table-primary'); thead.classList.add('table-secondary'); }
      }
    }
  };
  switchSkin(skin);
  useEffect(() => { switchSkin(skin); }, [skin]);

  const authHeader = { headers: { authorization: `Bearer ${getToken()}` } };

  const [roomInfo, setRoomInfo]     = useState(null);
  const [rows, setRows]             = useState([]);
  const [semester, setSemester]     = useState('1');
  const [loading2, setLoading2]     = useState(false);

  useEffect(() => {
    axiosInstance.get(`${API}/getRoomInfoForGrade/${room_id}`, authHeader)
    .then(res => setRoomInfo(res.data))
    .catch(() => {});
  }, [room_id]);

  const loadMatrix = useCallback(() => {
    setLoading(true);
    setLoading2(true);
    axiosInstance.get(`${API}/getClassGradeMatrix/${room_id}/${semester}/${year_id}`, authHeader)
    .then(res => setRows(res.data))
    .catch(err => {
      if (err.response?.status !== 404) {
        Swal.fire({ icon: 'error', title: 'เกิดข้อผิดพลาด', text: err.response?.data?.message || err.message });
      }
      setRows([]);
    })
    .finally(() => { setLoading(false); setLoading2(false); });
  }, [room_id, semester, year_id, setLoading]);

  useEffect(() => { loadMatrix(); }, [loadMatrix]);

  // Derive subjects (columns) and students (rows) from flat data
  const { subjects, students, gradeMap } = useMemo(() => {
    const subMap = new Map();
    const stMap  = new Map();
    const gMap   = {}; // gMap[study_id][sub_id] = { g_id, g_grade, g_waiting }

    rows.forEach(r => {
      if (!subMap.has(r.sub_id)) {
        subMap.set(r.sub_id, { sub_id: r.sub_id, sub_subid: r.sub_subid, sub_subname: r.sub_subname, sub_type: r.sub_type, sub_credit: r.sub_credit });
      }
      if (!stMap.has(r.study_id)) {
        stMap.set(r.study_id, { study_id: r.study_id, st_id: r.st_id, st_idnumber: r.st_idnumber, st_name: r.st_name, st_status: r.st_status });
      }
      if (!gMap[r.study_id]) gMap[r.study_id] = {};
      gMap[r.study_id][r.sub_id] = { g_id: r.g_id, g_grade: r.g_grade, g_waiting: r.g_waiting };
    });

    return {
      subjects: Array.from(subMap.values()),
      students: Array.from(stMap.values()),
      gradeMap: gMap,
    };
  }, [rows]);

  const handleExportPDF = async () => {
    const element = printRef.current;
    if (!element) return;
    const html2canvas = (await import('html2canvas')).default;

    const canvas = await html2canvas(element, { scale: 2, useCORS: true });
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const imgW = pageWidth - 10;
    const imgH = (canvas.height * imgW) / canvas.width;
    let y = 5;
    let remaining = imgH;
    while (remaining > 0) {
      pdf.addImage(imgData, 'PNG', 5, y, imgW, imgH);
      remaining -= (pageHeight - 10);
      if (remaining > 0) { pdf.addPage(); y = 5 - (imgH - remaining); }
    }
    pdf.save(`เกรดรวม_${roomLabel}_ภาค${semester}.pdf`);
  };

  const handlePrint = () => {
    const content = printRef.current?.innerHTML;
    if (!content) return;
    const win = window.open('', '_blank', 'width=1200,height=900');
    win.document.write(`
      <html><head><title>เกรดรวม</title>
      <style>
        body { font-family: Arial, sans-serif; font-size: 11px; }
        table { border-collapse: collapse; width: 100%; }
        th, td { border: 1px solid #999; padding: 3px 5px; text-align: center; }
        th { background: #ddeeff; }
        .rotate { writing-mode: vertical-rl; transform: rotate(-180deg); display: inline-block; white-space: nowrap; }
        .text-left { text-align: left !important; padding-left: 8px; }
        .text-danger { color: #dc3545; }
        .gpa-col { background: #f0eaff; }
        .score-cell { font-size: 15px !important; }
        .score-cell span { font-size: 15px !important; }
      </style>
      </head><body onload="window.print()">${content}</body></html>
    `);
    win.document.close();
  };

  const roomLabel = roomInfo
    ? `ม.${roomInfo.tb_class_c_id}/${roomInfo.r_no}`
    : `ห้อง ${room_id}`;

  return (
    <React.Fragment>
      <Header onSkin={setSkin} />
      <div className="main main-app p-3 p-lg-4">
        <div className="d-sm-flex align-items-center justify-content-between mb-4">
          <div>
            <ol className="breadcrumb fs-sm mb-1">
              <li className="breadcrumb-item"><Link to="#">วัดผลและประเมินผล</Link></li>
              <li className="breadcrumb-item"><Link to="/assessment/all-class-grade">เกรดรวมในชั้นเรียน</Link></li>
              <li className="breadcrumb-item active">{roomLabel}</li>
            </ol>
            <h4 className="main-title mb-0">
              เกรดรวม — {roomLabel}
              {roomInfo?.advisor_name && <span className="fs-sm text-muted ms-2">ครูประจำชั้น: {roomInfo.advisor_name}</span>}
            </h4>
          </div>
          <div className="d-flex align-items-center gap-2 mt-2 mt-sm-0">
            {roomInfo && (
              <span className="badge bg-primary fs-6 fw-normal">ปีการศึกษา {roomInfo.year_year}</span>
            )}
          </div>
        </div>

        <Row className="g-3">
          <Col xl="12">
            <Card className="card-one">
              <Card.Header className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                <Nav variant="tabs" activeKey={semester} onSelect={k => setSemester(k)}>
                  <Nav.Item><Nav.Link eventKey="1">ภาคเรียนที่ 1</Nav.Link></Nav.Item>
                  <Nav.Item><Nav.Link eventKey="2">ภาคเรียนที่ 2</Nav.Link></Nav.Item>
                </Nav>
                <div className="d-flex gap-1 ms-auto">
                  <Button variant="outline-primary" size="sm" onClick={handlePrint} disabled={students.length === 0}>
                    <i className="ri-printer-line me-1"></i>พิมพ์
                  </Button>
                  <Button variant="outline-danger" size="sm" onClick={handleExportPDF} disabled={students.length === 0}>
                    <i className="ri-file-pdf-line me-1"></i>ส่งออก PDF
                  </Button>
                </div>
              </Card.Header>

              <Card.Body className="p-2" style={{ overflowX: 'auto' }}>
                {loading2 ? (
                  <div className="text-center py-5"><Spinner animation="border" /></div>
                ) : students.length === 0 ? (
                  <div className="text-center text-muted py-5">ไม่พบข้อมูลผลการเรียนในภาคเรียนนี้</div>
                ) : (
                  <div ref={printRef}>
                    <div className="text-center mb-2" style={{ fontWeight: 'bold', fontSize: '20px' }}>
                      เกรดรวม {roomLabel} — ภาคเรียนที่ {semester}
                      {roomInfo && <span> ปีการศึกษา {roomInfo.year_year}</span>}
                    </div>
                    <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: subjects.length * 70 + 280 }}>
                      <thead>
                        <tr style={{ background: '#ddeeff' }}>
                          <th style={{ border: '1px solid #ccc', padding: '4px 6px', width: 40, textAlign: 'center' }}>ที่</th>
                          <th style={{ border: '1px solid #ccc', padding: '4px 6px', width: 100, textAlign: 'center' }}>เลขประจำตัว</th>
                          <th style={{ border: '1px solid #ccc', padding: '4px 8px', minWidth: 160, textAlign: 'left' }}>ชื่อ-สกุล</th>
                          {subjects.map(sub => (
                            <th key={sub.sub_id} style={{ border: '1px solid #ccc', padding: '3px 4px', width: 70, maxWidth: 70, textAlign: 'center', fontSize: '9px', wordBreak: 'break-word' }}>
                              <div style={{ lineHeight: 1.3 }}>
                                {sub.sub_subname}{sub.sub_type === 'extra' ? ' (พ)' : ''}
                              </div>
                              <div style={{ color: '#1a6bb5' }}>{sub.sub_subid}</div>
                            </th>
                          ))}
                          <th style={{ border: '1px solid #ccc', padding: '4px 6px', width: 60, textAlign: 'center', background: '#f0eaff' }}>GPA</th>
                        </tr>
                      </thead>
                      <tbody>
                        {students.map((st, idx) => {
                          const sg = gradeMap[st.study_id] || {};
                          const gpa = calcGPA(sg, subjects);
                          const hasWaiting = subjects.some(sub => sg[sub.sub_id]?.g_waiting === '1');
                          return (
                            <tr key={st.study_id} style={{ background: st.st_status === '0' ? '#fde8e8' : undefined }}>
                              <td style={{ border: '1px solid #ccc', padding: '3px', textAlign: 'center' }}>{idx + 1}</td>
                              <td style={{ border: '1px solid #ccc', padding: '3px', textAlign: 'center', color: '#1a6bb5' }}>{st.st_idnumber}</td>
                              <td style={{ border: '1px solid #ccc', padding: '3px 8px', textAlign: 'left' }}>{st.st_name}</td>
                              {subjects.map(sub => {
                                const entry = sg[sub.sub_id];
                                const grade = entry ? calcGrade(entry.g_grade) : null;
                                const hasScore = entry && entry.g_grade !== null;
                                const bg = !entry || !hasScore ? '#fde8e8' : grade > 0 ? '#d4edda' : '#fde8e8';
                                return (
                                  <td key={sub.sub_id} className="score-cell" style={{ border: '1px solid #ccc', padding: '3px 2px', textAlign: 'center', background: bg, fontSize: '11px' }}>
                                    {!entry ? (
                                      <span style={{ color: '#aaa' }}>-</span>
                                    ) : entry.g_waiting === '1' ? (
                                      <>
                                        <span style={{ color: 'blue' }}>{entry.g_grade}</span>{' '}
                                        <span style={{ color: 'red', fontWeight: 'bold' }}>ร</span>
                                      </>
                                    ) : (
                                      <>
                                        {hasScore && <span style={{ color: 'blue' }}>{entry.g_grade}</span>}
                                        {!hasScore && <span style={{ color: '#aaa' }}>-</span>}
                                        {' '}
                                        {grade !== null && hasScore && (
                                          <span style={{ fontWeight: 'bold', color: grade >= 1 ? '#155724' : '#721c24' }}>{grade}</span>
                                        )}
                                      </>
                                    )}
                                  </td>
                                );
                              })}
                              <td style={{ border: '1px solid #ccc', padding: '3px', textAlign: 'center', background: hasWaiting ? '#dc3545' : '#f0eaff', color: hasWaiting ? '#fff' : undefined, fontWeight: 'bold' }}>
                                {gpa}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <div className="mt-3 d-flex justify-content-end">
          <Link to="/assessment/all-class-grade" className="btn btn-secondary btn-sm">
            <i className="ri-arrow-left-line me-1"></i>ย้อนกลับ
          </Link>
        </div>
        <Footer />
      </div>
    </React.Fragment>
  );
}
